import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Download } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function MemberCard({ user, logout }) {
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMemberCard();
  }, []);

  const fetchMemberCard = async () => {
    try {
      const response = await axios.get(`${API}/member/card`, {
        withCredentials: true
      });
      setMember(response.data);
    } catch (error) {
      console.error("Error fetching member card:", error);
      if (error.response?.status === 404) {
        toast.error("Membership card not generated yet. Contact admin.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <header className="bg-slate-950 border-b border-white/5">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/dashboard">
            <Button variant="ghost" className="text-yellow-400 hover:text-yellow-300" data-testid="back-button">
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
          </Link>
          <img src="/branding/football-and-more-white.png" alt="Football And More" className="h-8 w-auto" data-testid="member-card-header-logo" />
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-md mx-auto">
          <h2 className="text-3xl font-bold text-white mb-8 text-center" data-testid="page-title">Membership Card</h2>

          {member ? (
            <Card className="force-dark p-8 bg-black shadow-2xl border-4 border-yellow-500/30 relative overflow-hidden" data-testid="membership-card">
              {/* Background Logo */}
              <div className="absolute inset-0 flex items-center justify-center opacity-10">
                <img 
                  src="/branding/football-and-more-white.png"
                  alt="Football And More Logo Background"
                  className="w-72 h-auto"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>

              {/* Content */}
              <div className="relative z-10">
                <div className="text-center mb-6">
                  <img 
                    src="/branding/football-and-more-white.png"
                    alt="Football And More"
                    className="h-14 w-auto mx-auto mb-3"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="hidden w-20 h-20 mx-auto mb-3 items-center justify-center">
                    <span className="text-yellow-500 text-3xl font-bold">F&amp;M</span>
                  </div>
                  <p className="text-sm text-yellow-300 uppercase tracking-wide">{member.member_type === 'section' ? 'Section Member' : 'Official Member'}</p>
                </div>

                <div className="space-y-4 mb-6">
                  <div>
                    <p className="text-sm text-yellow-200">Member Name</p>
                    <p className="text-lg font-bold text-yellow-400" data-testid="member-name">{user.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-yellow-200">Membership Number</p>
                    <p className="text-xl font-bold text-yellow-400" data-testid="membership-number">{member.membership_number}</p>
                  </div>
                  <div>
                    <p className="text-sm text-yellow-200">Member Type</p>
                    <p className="text-lg font-bold text-yellow-400 capitalize" data-testid="member-type">
                      {member.member_type === 'section' ? 'Section Member' : 'Non-Section Member'}
                    </p>
                  </div>
                  
                  {/* Team Information */}
                  {(member.primary_team || member.second_team) && (
                    <div className="border-t border-yellow-500/20 pt-4 mt-4">
                      <p className="text-sm text-yellow-200 mb-2">Team Registration</p>
                      {member.primary_team && (
                        <div className="flex items-center gap-2 mb-2">
                          <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 text-xs font-bold rounded">1ST</span>
                          <p className="text-lg font-bold text-yellow-400" data-testid="primary-team">{member.primary_team}</p>
                        </div>
                      )}
                      {member.second_team && (
                        <div className="flex items-center gap-2">
                          <span className="px-2 py-1 bg-slate-500/30 text-slate-300 text-xs font-bold rounded">2ND</span>
                          <p className="text-lg font-bold text-slate-300" data-testid="second-team">{member.second_team}</p>
                        </div>
                      )}
                      {!member.primary_team && !member.second_team && (
                        <p className="text-slate-400 italic">Not assigned to any team</p>
                      )}
                    </div>
                  )}
                  
                  <div>
                    <p className="text-sm text-yellow-200">Member Since</p>
                    <p className="text-lg font-bold text-yellow-400" data-testid="joined-date">
                      {new Date(member.joined_date).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-yellow-200">Status</p>
                    <p className="text-lg font-bold text-green-400 capitalize" data-testid="member-status">{member.status}</p>
                  </div>
                </div>

                <div className="flex justify-center mb-4">
                  <div className="bg-slate-900/50 p-4 rounded-lg border-4 border-yellow-500/30">
                    <img
                      src={`data:image/png;base64,${member.qr_code}`}
                      alt="Membership QR Code"
                      className="w-48 h-48"
                      data-testid="qr-code-image"
                    />
                  </div>
                </div>

                <p className="text-xs text-yellow-300 text-center">
                  Show this QR code for membership verification and discounts
                </p>
              </div>
            </Card>
          ) : (
            <Card className="p-8 bg-slate-900/50 text-center" data-testid="no-card-message">
              <p className="text-slate-400">Membership card not available yet. Please contact admin.</p>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}